/**
 * The Leads view, as data: which columns exist, which filters exist, what a
 * saved view is, and whether the table currently matches one.
 *
 * The filters are DECLARED here and APPLIED in SQL (src/leadViews.js →
 * leadFilterSql). There is deliberately no matcher in this file: the list shows
 * a capped page of ~1,560 leads, so a filter evaluated in the browser answers a
 * question about the page rather than about the table, and its count says "of
 * 500" while claiming to say "of all your leads".
 *
 * Import-free by design (like timelineModel.mjs) so the browser bundle and
 * `node --test` load the exact same file. That is also why labels live here but
 * cells do not: a column's header text is a string, its cell is JSX, so the
 * renderers stay in LeadsListPage and are keyed off LEAD_COLUMNS.
 *
 * The pill/label vocabularies (stage, channel, source, company status, pipeline
 * stage) belong to ui.js. A filter that draws its options from one of them says
 * so with `labels`, and holds only the VALUES here — the values are what a saved
 * view stores and what the server validates, and they must not drift into being
 * whatever the label map happens to contain.
 *
 * src/leadViews.js holds the server's copy of these key/value whitelists (the
 * client's src scope means the two cannot share a module) and
 * test/lead-views.test.js asserts the two agree.
 */

// A saved view is cheap; a view list you scroll is not a view list.
export const MAX_VIEWS = 12;
export const MAX_VIEW_NAME = 40;

/**
 * Every column the table can show, in the order the picker offers them.
 *
 * `sort` is the server-side sort key from CONTACT_SORTS (src/db/crm.js) — a
 * column without one is not sortable, and that is a deliberate answer rather
 * than an omission in three places: employee_range and pipeline stage are text
 * whose alphabetical order is not their real order ("11-50" before "5001+",
 * "agreement" before "new"), and phone/LinkedIn/touch detail are values you
 * filter on, never ones you rank by.
 *
 * `num` puts the cell in tabular figures — dates, money and counts only.
 */
export const LEAD_COLUMNS = [
  { key: "name", label: "Name", sort: "name", group: "person" },
  { key: "company", label: "Company", sort: "company", group: "person" },
  { key: "job_title", label: "Job title", sort: "job_title", group: "person" },
  { key: "email", label: "Email", sort: "email", group: "person" },
  { key: "phone", label: "Phone", group: "person" },
  { key: "mobile", label: "Mobile", group: "person" },
  { key: "linkedin", label: "LinkedIn", group: "person" },
  // Since when Gabriel is connected to them, read from LinkedIn's own
  // connection emails (src/linkedinConnections.js).
  { key: "li_connected", label: "LinkedIn connected", sort: "li_connected", num: true, group: "person" },
  { key: "person_location", label: "Person's location", group: "person" },
  { key: "stage", label: "Stage", sort: "stage", group: "lead" },
  { key: "channel", label: "Channel", sort: "channel", group: "lead" },
  { key: "source", label: "First touch", sort: "source", group: "lead" },
  { key: "touch_detail", label: "Touch detail", group: "lead" },
  // Has this lead ever written back — the `connected` filter's own predicate,
  // shown as a cell.
  { key: "replied", label: "Replied", sort: "replied", group: "lead" },
  // The HubSpot-era lead status, as HubSpot spelled it. Not the stage.
  { key: "lead_status", label: "Lead status (HubSpot)", sort: "lead_status", group: "lead" },
  { key: "company_status", label: "Company status", group: "company" },
  { key: "status_reason", label: "Status reason", sort: "status_reason", group: "company" },
  { key: "pipeline_stage", label: "Pipeline", group: "company" },
  // NOT "Value": monthly_value is the company's own ad budget while they are a
  // prospect and our retainer once they are a client, and this column shows
  // both — so the header names both and the cell's tooltip says which this row
  // is. "Value" read as deal value, which it is not.
  { key: "value", label: "Budget / retainer", sort: "value", num: true, group: "company" },
  { key: "domain", label: "Domain", sort: "domain", group: "company" },
  { key: "company_linkedin", label: "Company LinkedIn", group: "company" },
  { key: "recycle_until", label: "Recycled until", sort: "recycle_until", num: true, group: "company" },
  { key: "re_engage_after", label: "Re-engage after", sort: "re_engage_after", num: true, group: "company" },
  { key: "won", label: "Won", sort: "won", num: true, group: "company" },
  { key: "churned", label: "Churned", sort: "churned", num: true, group: "company" },
  { key: "signed_fee", label: "Signed fee", group: "company" },
  { key: "signed_terms", label: "Signed terms", group: "company" },
  { key: "agreement", label: "Agreement", group: "company" },
  { key: "audited", label: "LinkedIn audit", sort: "audited", num: true, group: "company" },
  { key: "company_last_activity", label: "Company last activity", sort: "company_last_activity", num: true, group: "company" },
  { key: "company_created", label: "Company created", sort: "company_created", num: true, group: "company" },
  { key: "industry", label: "Industry", sort: "industry", group: "firmographics" },
  { key: "employees", label: "Employees", group: "firmographics" },
  { key: "country", label: "Country", sort: "country", group: "firmographics" },
  { key: "company_city", label: "City", group: "firmographics" },
  { key: "timezone", label: "Timezone", group: "firmographics" },
  { key: "founded", label: "Founded", sort: "founded", num: true, group: "firmographics" },
  { key: "revenue", label: "Annual revenue", sort: "revenue", num: true, group: "firmographics" },
  { key: "funding", label: "Funding raised", group: "firmographics" },
  { key: "description", label: "Description", group: "firmographics" },
  { key: "tech", label: "Tech stack", group: "firmographics" },
  // What they told us on the form. Most of these are HubSpot-era answers kept in
  // contacts.properties; referred_by / heard_about also read intake's own
  // properties.self_reported first.
  { key: "li_ads", label: "Running LinkedIn ads?", group: "form" },
  // Bands ("Under $10,000", "$100,000+"), so not sortable: alphabetical is not
  // their order, same as employees.
  { key: "li_budget", label: "LinkedIn budget (form)", group: "form" },
  { key: "heard_about", label: "Heard about us", group: "form" },
  { key: "referred_by", label: "Referred by", group: "form" },
  { key: "first_form", label: "First form", group: "form" },
  { key: "first_conversion", label: "First conversion", sort: "first_conversion", num: true, group: "form" },
  { key: "intro_call_set", label: "Intro call set (HubSpot)", sort: "intro_call_set", num: true, group: "form" },
  { key: "last_activity", label: "Last activity", sort: "last_activity", num: true, group: "dates" },
  { key: "created", label: "Created", sort: "created", num: true, group: "dates" },
  { key: "updated", label: "Updated", sort: "updated", num: true, group: "dates" },
];

// The picker's sections, in order. A flat list of fifty is a list you scroll
// past the column you wanted.
export const LEAD_COLUMN_GROUPS = [
  ["person", "Person"],
  ["lead", "Lead"],
  ["company", "Company"],
  ["firmographics", "Firmographics"],
  ["form", "What they told us"],
  ["dates", "Dates"],
];

export const LEAD_COLUMN_KEYS = LEAD_COLUMNS.map((c) => c.key);
export const LEAD_COLUMN_BY_KEY = new Map(LEAD_COLUMNS.map((c) => [c.key, c]));

// The nine columns the table shipped with, unchanged: the default view is the
// view it always was, and everything else is opt-in.
export const DEFAULT_LEAD_COLUMNS = [
  "name", "company", "job_title", "stage", "channel", "source", "last_activity", "created",
];

/**
 * Newest leads first.
 *
 * It used to be updated_at, inherited from the days before the list was
 * sortable — but updated_at moves whenever anything touches the row, including
 * a watcher stamping last_activity or an enrichment filling a blank, so the top
 * of the list was "whatever the system last thought about" rather than "what
 * came in". created_at is the question a lead list is opened with, and it is the
 * one column whose order never changes under you.
 *
 * Created is one of the default columns, so the arrow now sits on a header you
 * can see — the old default sorted by a column the table did not show.
 */
export const DEFAULT_LEAD_SORT = { key: "created", dir: "desc" };

// 'updated' stays in the whitelist even when no column carries it: it is the
// server's own fallback (CONTACT_SORTS.updated) and a saved view may still hold
// it.
export const LEAD_SORT_KEYS = ["updated", ...LEAD_COLUMNS.filter((c) => c.sort).map((c) => c.sort)]
  .filter((k, i, all) => all.indexOf(k) === i);

/**
 * The filters, in the order they are offered.
 *
 * `primary` filters sit in the always-visible band; the rest live behind "More
 * filters" and show as removable chips while set. That split is about the band
 * staying scannable, not about importance — a secondary filter is as real as a
 * primary one and saves into a view identically.
 *
 * `values` + `labels` = a closed vocabulary labelled from ui.js.
 * `options`          = a closed vocabulary whose labels exist only here.
 * `open: true`       = free data (industry, country); its options are the
 *                      server's facet values, so every option matches something
 *                      and the list does not shrink as other filters narrow.
 * `multi: true`      = holds an ARRAY of values, ORed together.
 *
 * WHICH FILTERS ARE MULTI, and why not all of them: a filter naming an
 * ATTRIBUTE takes several values, because "prospect or client" is a real
 * question and one a single select cannot ask. A filter naming a WINDOW or a
 * MODE does not — "created in the last 30 days or the last 90 days" is just the
 * last 90, and "disqualified hidden or only disqualified" is a contradiction.
 * So stage, channel, source, company status, pipeline stage, ever in stage,
 * missing, industry and country are multi; q, activity, connected, audited, li_connected, created and exits stay single.
 *
 * Values within one filter OR; the filters themselves AND. That is the whole
 * combination rule, and it is the one every list like this uses.
 */
export const LEAD_FILTERS = [
  { key: "q", kind: "text", primary: true, label: "Search", placeholder: "Search name, company, title" },
  {
    key: "stage", multi: true, primary: true, label: "Stage", all: "All stages", labels: "stage",
    // churned = a customer whose company churned (shownStage in ui.js).
    values: ["lead", "marketingqualifiedlead", "salesqualifiedlead", "opportunity", "customer", "churned"],
  },
  {
    // 'form' is every lead who filled one — the ~20 the CRM recorded at intake
    // and the ~1,072 migrated ones whose channel is derived from the HubSpot
    // form name (src/leadChannel.js). The newsletter signups are 'other', not
    // 'form': subscribing is not asking for a proposal.
    key: "channel", multi: true, primary: true, label: "Channel", all: "All channels", labels: "channel",
    values: ["website_form", "intro_email", "linkedin_dm", "whatsapp_dm", "other"],
  },
  {
    // 'other' is the contacts we could not attribute (lead_source IS NULL) —
    // they show no source pill, so the filter names them rather than pretending
    // the column has a fourth value.
    key: "source", multi: true, primary: true, label: "First touch", all: "All sources", labels: "source",
    values: ["linkedin", "referral", "organic", "other"],
  },
  {
    key: "activity", primary: true, label: "Activity", all: "Any activity",
    options: [["7", "Active this week"], ["30", "Active this month"], ["stale", "Quiet 30d+"]],
  },
  {
    // Did we ever reach this person — did they write back, or did we get on a
    // call? The form they arrived through is not an answer (src/db/mailFilters
    // → connectedTouch says why), so "Never connected" over the form channel is
    // the pile of inbound leads that raised a hand and then went quiet.
    //
    // A MODE, not an attribute: "connected or never connected" is every lead,
    // so this one is single-select like activity and exits.
    //
    // READ IT WITH A DATE IN MIND. The answer is only as good as the history:
    // email and meetings were imported from HubSpot, which barely logged them
    // before 2025, so a 2022 lead reads "never connected" when what we really
    // know is that nothing was recorded. Pair it with Created for a live
    // question — docs/BACKLOG.md holds the note.
    key: "connected", label: "Ever connected", all: "Connected or not",
    options: [["yes", "Connected"], ["no", "Never connected"]],
  },
  {
    // Their LinkedIn Ads account, audited by us — viewer access given and the
    // audit run, marked by hand on the record (companies.audited_at). A mode,
    // single-select, for the same reason as connected.
    key: "audited", label: "LinkedIn audit", all: "Audited or not",
    options: [["yes", "Audited"], ["no", "Not audited"]],
  },
  {
    // Connected to Gabriel on LinkedIn, per the emails LinkedIn sends when a
    // connection lands. The other side reads "No connection email", not "Not
    // connected": an invitation he accepted sends him nothing, so absence is
    // unknown rather than no. A mode, single-select.
    key: "li_connected", label: "LinkedIn connection", all: "Connected or not",
    options: [["yes", "Connected on LinkedIn"], ["no", "No connection email"]],
  },
  {
    // The row state the table already derives from the company. Hiding them is
    // the common case — a working lead list is the live ones — but the default
    // stays "include", because a list that silently drops rows is worse than a
    // longer one.
    key: "exits", label: "Disqualified & lost", all: "Included",
    options: [["hide", "Hidden"], ["only", "Only these"]],
  },
  {
    key: "company_status", multi: true, label: "Company status", all: "Any status", labels: "company_status",
    values: ["prospect", "client", "churned", "lost", "not_a_fit", "advisory", "vendor", "recycled"],
  },
  {
    // The six OPEN stages, which is all companies.pipeline_stage can hold —
    // the board's closed columns are derived from status (client / lost), so
    // "closed won" as a pipeline stage here would be a filter matching nothing.
    // Company status is where you ask that question.
    key: "pipeline_stage", multi: true, label: "Pipeline stage", all: "Any stage", labels: "pipeline_stage",
    values: ["new", "call_scheduled", "in_progress", "proposal_sent", "evaluating", "agreement"],
  },
  {
    // Ever in a board column — now, or at any point in stage_transitions. The
    // board's column heads open the list on this. It takes the board's eight
    // columns, closed won/lost included, because those are the heads you click;
    // src/leadViews.js → everStageSql says what counts as "was there" and how
    // far back the history reaches (pipeline moves from Feb 2026 only).
    key: "ever_stage", multi: true, label: "Ever in stage", all: "Any history", labels: "pipeline_stage",
    values: ["new", "call_scheduled", "in_progress", "proposal_sent", "evaluating", "agreement", "closed_won", "closed_lost"],
  },
  {
    key: "created", label: "Created", all: "Any time",
    options: [["30", "Last 30 days"], ["90", "Last 90 days"], ["ytd", "This year"], ["old90", "Older than 90 days"]],
  },
  {
    // What is MISSING, for enrichment sweeps — the one question the old band
    // could not ask at all, and the reason a filter set is worth saving.
    key: "missing", multi: true, label: "Missing", all: "Nothing in particular",
    options: [["email", "No email"], ["job_title", "No job title"], ["company", "No company"],
      ["linkedin", "No LinkedIn URL"], ["phone", "No phone"]],
  },
  // `open` = free data rather than a vocabulary. Their options are the facet
  // values the server sends back (every industry / country attached to a lead),
  // so the list is the whole table's and does not shrink as other filters narrow
  // the page.
  { key: "industry", label: "Industry", all: "Any industry", open: true, multi: true },
  { key: "country", label: "Country", all: "Any country", open: true, multi: true },
];

export const LEAD_FILTER_KEYS = LEAD_FILTERS.map((f) => f.key);
export const LEAD_FILTER_BY_KEY = new Map(LEAD_FILTERS.map((f) => [f.key, f]));
export const PRIMARY_LEAD_FILTERS = LEAD_FILTERS.filter((f) => f.primary);
export const SECONDARY_LEAD_FILTERS = LEAD_FILTERS.filter((f) => !f.primary);

export const MULTI_LEAD_FILTERS = new Set(LEAD_FILTERS.filter((f) => f.multi).map((f) => f.key));

export const isMultiLeadFilter = (key) => MULTI_LEAD_FILTERS.has(key);

// Every filter empty: [] for a multi filter, "" for a single one — never
// undefined, so an input stays controlled either way.
export function blankLeadFilters() {
  return Object.fromEntries(LEAD_FILTER_KEYS.map((k) => [k, isMultiLeadFilter(k) ? [] : ""]));
}

// One filter's value, always as an array — which is what the query string, the
// chips and the checkboxes all want. A single filter reads as a one-value set,
// and so does a multi filter a stored view left as a bare string.
export function leadFilterValues(filters, key) {
  const raw = filters?.[key];
  if (Array.isArray(raw)) return raw.filter((v) => v !== "" && v !== null && v !== undefined).map(String);
  return raw === "" || raw === null || raw === undefined ? [] : [String(raw)];
}

// Only the filters that are actually set — what a view stores, what the chips
// render, and what goes on the query string. An empty array is not a filter.
export function activeLeadFilters(filters) {
  return LEAD_FILTER_KEYS
    .map((k) => [k, leadFilterValues(filters, k)])
    .filter(([, values]) => values.length);
}

/**
 * Tick or untick one value.
 *
 * The result keeps the VOCABULARY's order rather than click order, so the chip,
 * the URL and a saved view all read the same way however the boxes were
 * clicked — and two people ticking the same three boxes in different orders get
 * the same view, which is what makes "edited" mean anything.
 */
export function toggleLeadFilterValue(filters, key, value) {
  const def = LEAD_FILTER_BY_KEY.get(key);
  const current = leadFilterValues(filters, key);
  const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
  if (!isMultiLeadFilter(key)) return { ...filters, [key]: next.length ? next[next.length - 1] : "" };
  const order = def?.values || (def?.options || []).map(([v]) => v);
  const sorted = order.length
    ? next.slice().sort((a, b) => order.indexOf(a) - order.indexOf(b))
    : next.slice().sort((a, b) => a.localeCompare(b));
  return { ...filters, [key]: sorted };
}

/**
 * A stored view, made safe to apply. Views are persisted JSON that outlives the
 * code that wrote them, so applying one drops what no longer exists (a removed
 * column, a filter whose vocabulary changed) rather than rendering a table with
 * a hole in it. The server validates on the way in; this validates on the way
 * out, because the two events are months apart.
 */
export function normalizeLeadView(raw) {
  const columns = (Array.isArray(raw?.columns) ? raw.columns : [])
    .filter((k, i, all) => LEAD_COLUMN_BY_KEY.has(k) && all.indexOf(k) === i);
  const filters = blankLeadFilters();
  for (const [key, raw_value] of Object.entries(raw?.filters || {})) {
    const def = LEAD_FILTER_BY_KEY.get(key);
    if (!def) continue;
    // A view written before this filter took several values holds a bare
    // string, and one written before a filter stopped taking several holds an
    // array. Both still mean something, so both are read.
    const vocabulary = def.values || (def.options ? def.options.map(([v]) => v) : null);
    const values = leadFilterValues({ [key]: raw_value }, key)
      .filter((v) => !vocabulary || vocabulary.includes(v))
      .filter((v, i, all) => all.indexOf(v) === i);
    if (!values.length) continue;
    filters[key] = isMultiLeadFilter(key) ? values : values[0];
  }
  const sort = LEAD_SORT_KEYS.includes(raw?.sort?.key)
    ? { key: raw.sort.key, dir: raw.sort.dir === "asc" ? "asc" : "desc" }
    : { ...DEFAULT_LEAD_SORT };
  return {
    id: String(raw?.id || ""),
    name: String(raw?.name || "").slice(0, MAX_VIEW_NAME),
    filters,
    columns: columns.length ? columns : [...DEFAULT_LEAD_COLUMNS],
    sort,
  };
}

// The view every list starts from: no filters, the original columns, newest
// first. It is not stored — it is what "no view" means.
export function defaultLeadViewState() {
  return { filters: blankLeadFilters(), columns: [...DEFAULT_LEAD_COLUMNS], sort: { ...DEFAULT_LEAD_SORT } };
}

/**
 * Is the table still showing what this view says? Drives the "edited" state, so
 * it compares exactly the three things a view stores and nothing else.
 */
export function sameLeadViewState(a, b) {
  const fa = activeLeadFilters(a?.filters);
  const fb = activeLeadFilters(b?.filters);
  if (fa.length !== fb.length) return false;
  // Values are compared in order, which they can be because toggling keeps them
  // in the vocabulary's order rather than the order they were clicked.
  if (fa.some(([k, v], i) => fb[i][0] !== k || v.join("\u0000") !== fb[i][1].join("\u0000"))) return false;
  const ca = a?.columns || [];
  const cb = b?.columns || [];
  if (ca.length !== cb.length || ca.some((k, i) => cb[i] !== k)) return false;
  return a?.sort?.key === b?.sort?.key && a?.sort?.dir === b?.sort?.dir;
}

/**
 * Slug for a new view, from its name. Views are addressed by id in settings and
 * in the active-view pointer, so the id has to be stable and collision-free;
 * a name that slugs to nothing (all punctuation, or another language) still
 * gets an id, because refusing to save a view over its name is absurd.
 */
export function leadViewId(name, taken = []) {
  const base = String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32) || "view";
  const used = new Set(taken);
  if (!used.has(base)) return base;
  for (let n = 2; n < 100; n++) {
    if (!used.has(`${base}-${n}`)) return `${base}-${n}`;
  }
  return `${base}-${used.size + 1}`;
}
